import React, { useState } from 'react';
import { ArrowRight, CheckCircle, Award, Users, Shield, Wrench } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo, services } from '../data/mock';
import { serviceImages, engineeringImages } from '../data/images';
import ContactModal from '../components/ContactModal';

const HomePageIndustrial = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const whyChooseFeatures = [
    { icon: Award, title: '24/7 Support at Any Time' },
    { icon: Shield, title: 'Cost-Effectiveness' },
    { icon: Users, title: 'Guarantees to Count On' },
    { icon: Wrench, title: 'Eco-Friendly Material' }
  ];

  const stats = [
    { value: '500+', label: 'Projects Completed' },
    { value: '98%', label: 'Client Satisfaction' },
    { value: '15+', label: 'Years Experience' },
    { value: '50+', label: 'Expert Team Members' }
  ];

  const processSteps = [
    {
      number: '01',
      title: 'Preparation Of Materials',
      description: 'Moreover by automating repetitive tasks, you can free up your workforce for more strategic activities.'
    },
    {
      number: '02',
      title: 'Component Sourcing',
      description: 'Clear processes reduce confusion and keep teams focused on achieving their goals effectively.'
    },
    {
      number: '03',
      title: 'Quality Control',
      description: 'Modern tools ensure reliability and meet industry standards by default with precision.'
    },
    {
      number: '04',
      title: 'Final Assembly',
      description: 'Proven workflows ensure seamless completion and product readiness for market delivery.'
    }
  ];

  return (
    <div className="home-page-industrial">
      {/* Hero Section */}
      <section className="hero-industrial">
        <div className="hero-industrial-bg">
          <img
            src="https://images.unsplash.com/photo-1600869009498-8d429f88d4f5"
            alt="Engineering Workspace"
          />
          <div className="hero-industrial-overlay"></div>
        </div>

        <div className="container-main hero-industrial-content">
          <span className="section-label">Welcome to Carboniq</span>
          <h1 className="hero-industrial-title">
            Precision in Design.<br />
            Value in Every Detail.
          </h1>
          <p className="hero-industrial-subtitle">
            End-to-end mechanical design services from concept to design-for-manufacture.
            We deliver innovative solutions that bring your products to market faster.
          </p>
          <div className="flex flex-wrap gap-4">
            <button onClick={() => setIsModalOpen(true)} className="btn-industrial-primary">
              Get Started <ArrowRight size={20} />
            </button>
            <Link to="/services" className="btn-industrial-secondary">
              Our Services <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding" style={{ background: 'var(--bg-page)' }}>
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="section-label">Our Services</span>
            <h2 className="heading-1" style={{ color: 'var(--text-primary)' }}>
              We Create Personalized<br />Manufacturing Plans
            </h2>
          </div>

          <div className="services-industrial-grid">
            {services.slice(0, 6).map((service, idx) => (
              <div key={service.id} className="service-card-industrial">
                <div className="service-card-industrial-image">
                  <img
                    src={serviceImages[idx]}
                    alt={service.title}
                  />
                  <div className="service-icon-badge">
                    <Wrench size={32} />
                  </div>
                </div>
                <div className="service-card-industrial-body">
                  <h3 className="service-card-industrial-title">{service.title}</h3>
                  <p className="service-card-industrial-text">
                    {service.description.substring(0, 120)}...
                  </p>
                  <Link to="/services" className="service-read-more">
                    Read More <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" className="btn-industrial-primary">
              Explore All Services <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-choose-section section-padding">
        <div className="container-main">
          <div className="why-choose-grid">
            <div className="why-choose-image">
              <img
                src={engineeringImages.fea}
                alt="Engineering Excellence"
              />
            </div>

            <div className="why-choose-content">
              <span className="section-label">Why Choose Us</span>
              <h2>We Are Leader In Industrial Market Since 1995</h2>
              <p className="why-choose-description">
                Carboniq is a full-service manufacturing company with 15 years of experience 
                serving industries such as automotive, heavy equipment, and consumer products.
              </p>

              <div className="why-choose-features">
                {whyChooseFeatures.map((feature, idx) => {
                  const IconComponent = feature.icon;
                  return (
                    <div key={idx} className="feature-item-industrial">
                      <div className="feature-icon-industrial">
                        <IconComponent size={24} />
                      </div>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                        {feature.title}
                      </span>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-4">
                <Link to="/about" className="btn-industrial-primary">
                  Discover More <ArrowRight size={20} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="section-padding" style={{ background: 'var(--bg-page)' }}>
        <div className="container-main">
          <div className="stats-bar">
            {stats.map((stat, idx) => (
              <div key={idx} className="stat-item-industrial">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Working Process */}
      <section className="section-padding" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="container-main">
          <div className="text-center mb-16">
            <span className="section-label">Working Process</span>
            <h2 className="heading-1" style={{ color: 'var(--text-primary)' }}>
              We Create Personalized<br />Industry Solutions
            </h2>
          </div>

          <div className="process-grid">
            {processSteps.map((step, idx) => (
              <div key={idx} className="process-card">
                <div className="process-number">{step.number}</div>
                <h3 className="process-title">{step.title}</h3>
                <p className="process-description">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" style={{ background: 'var(--bg-page)' }}>
        <div className="container-main">
          <div style={{
            background: 'var(--primary)',
            padding: '4rem 3rem',
            textAlign: 'center',
            borderRadius: '8px'
          }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              fontWeight: 900,
              color: 'var(--text-inverse)',
              marginBottom: '1rem'
            }}>
              Ready to Start Your Project?
            </h2>
            <p style={{
              fontSize: '1.125rem',
              color: 'rgba(15, 27, 41, 0.8)',
              marginBottom: '2rem',
              maxWidth: '600px',
              margin: '0 auto 2rem'
            }}>
              Let's discuss how we can help bring your mechanical design vision to life
            </p>
            <button
              onClick={() => setIsModalOpen(true)}
              style={{
                background: 'var(--text-inverse)',
                color: 'var(--primary)',
                padding: '1.125rem 2.5rem',
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'uppercase',
                border: 'none',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.75rem'
              }}
            >
              Get Started Today <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      {/* Contact Modal */}
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HomePageIndustrial;
